// Shared host-side game leaderboard with animated player rows and stat columns.

import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';

import { ScrollArea } from '@/components/ui/scroll-area';
import { useMedia } from '@/hooks';
import { resolvePlayerPalette } from '@/styles/playerColors';
import { cn } from '@/utilities/cn';

export interface LeaderboardStat {
  key: string;
  label: string;
  value: number;
}

export interface LeaderboardRow {
  playerId: string;
  playerName: string;
  playerColor?: string;
  /** Optional game-specific role marker rendered immediately after the name. */
  playerNameIcon?: {
    icon: LucideIcon;
    label: string;
  };
  stats: LeaderboardStat[];
}

export interface GameLeaderboardProps {
  rows: LeaderboardRow[];
  mode?: 'turn' | 'game';
}

const STAT_CELL_CLASS =
  'relative flex h-12 min-w-[140px] shrink-0 items-center justify-center rounded-lg max-md:min-w-24';

function getPlayerOrder(rows: LeaderboardRow[]) {
  return rows.map((row) => row.playerId);
}

function hasOrderChanged(previousOrder: string[], currentOrder: string[]) {
  return (
    previousOrder.length !== currentOrder.length ||
    previousOrder.some((playerId, index) => currentOrder[index] !== playerId)
  );
}

function GameLeaderboard({ rows, mode }: GameLeaderboardProps) {
  const { playSoundEffect } = useMedia();
  const previousModeRef = useRef<typeof mode>(mode);
  const previousOrderRef = useRef<string[]>(getPlayerOrder(rows));

  useEffect(() => {
    const previousMode = previousModeRef.current;
    const previousOrder = previousOrderRef.current;
    const currentOrder = getPlayerOrder(rows);

    if (
      previousMode === 'turn' &&
      mode === 'game' &&
      hasOrderChanged(previousOrder, currentOrder)
    ) {
      playSoundEffect('whistle_slide');
    }

    previousModeRef.current = mode;
    previousOrderRef.current = currentOrder;
  }, [mode, playSoundEffect, rows]);

  if (rows.length === 0) {
    return null;
  }

  const headerStats = rows[0].stats;

  return (
    <div className="absolute inset-0 z-[300] box-border flex h-[88vh] w-full items-center justify-center overflow-hidden">
      <div className="box-border mt-40 mb-40 flex h-[calc(100vh-400px)] w-full max-w-[920px] flex-col overflow-hidden rounded-lg bg-neutral-950 p-6 max-md:mt-[120px] max-md:mb-[120px] max-md:h-[calc(100vh-280px)] max-md:p-4">
        <div className="mb-4 flex items-center justify-between gap-4 border-b-2 border-neutral-700 px-6 py-4">
          <div className="min-w-0 flex-1 text-left text-lg font-bold text-white max-md:text-base">
            Player
          </div>
          {headerStats.map((stat) => (
            <div
              key={stat.key}
              className="min-w-[140px] text-center text-lg font-bold text-white max-md:min-w-24 max-md:text-base"
            >
              {stat.label}
            </div>
          ))}
        </div>

        <div className="relative min-h-0 flex-1 overflow-hidden bg-transparent">
          <ScrollArea className="h-full min-h-0">
            <div className="flex flex-col gap-2 bg-transparent">
              <AnimatePresence initial={false}>
                {rows.map((row) => {
                  const { palette } = resolvePlayerPalette(row);
                  const PlayerNameIcon = row.playerNameIcon?.icon;

                  return (
                    <motion.div
                      key={row.playerId}
                      className="flex min-h-14 items-center justify-between gap-4 rounded-lg px-6 py-4 first:mt-4 last:mb-4"
                      style={{ background: palette.dark }}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      layout
                      transition={{
                        layout: { duration: 0.6, ease: 'easeInOut' },
                        opacity: { duration: 0.3 },
                        x: { duration: 0.3 },
                      }}
                    >
                      <div className="flex min-h-12 min-w-0 flex-1 items-center">
                        <span
                          className="truncate text-xl leading-tight font-bold max-md:text-base"
                          style={{ color: palette.base }}
                        >
                          {row.playerName}
                        </span>
                        {PlayerNameIcon && (
                          <PlayerNameIcon
                            aria-label={row.playerNameIcon?.label}
                            role="img"
                            className="ml-2 size-5 shrink-0 max-md:size-4"
                            style={{ color: palette.base }}
                          />
                        )}
                      </div>

                      {row.stats.map((stat, index) => {
                        const isTotalColumn = index === row.stats.length - 1;

                        return (
                          <div
                            key={stat.key}
                            className={cn(
                              STAT_CELL_CLASS,
                              isTotalColumn && 'border-2'
                            )}
                            style={
                              isTotalColumn
                                ? { borderColor: palette.base }
                                : undefined
                            }
                          >
                            <div
                              className="absolute inset-0 rounded-lg opacity-[0.16]"
                              style={{ background: palette.light }}
                            />
                            <motion.span
                              key={`${row.playerId}-${stat.key}-${stat.value}`}
                              className="relative z-10 text-center text-xl font-bold max-md:text-base"
                              style={{ color: palette.base }}
                              initial={{ scale: 1.2, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              transition={{ duration: 0.3 }}
                            >
                              {stat.value}
                            </motion.span>
                          </div>
                        );
                      })}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}

export default GameLeaderboard;
